import React, { useState, useEffect } from 'react';
import './ContactInsights.css';

function ContactInsights({ user, dateRange, stage }) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInsights();
  }, [dateRange, stage]);

  const fetchInsights = async () => {
    try {
      const response = await fetch(
        `/backend/api/tasks/contact-insights.php?date_range=${dateRange}&stage=${stage}`,
        { credentials: 'include' }
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = await response.json();
      if (result.success) {
        setInsights(result);
      }
    } catch (error) {
      console.error('Error fetching contact insights:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'lead': return '#3b82f6';
      case 'customer': return '#10b981';
      case 'marketer': return '#f59e0b';
      case 'leader': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  const getStageIcon = (stage) => {
    switch (stage) {
      case 'lead': return '👥';
      case 'customer': return '🛒';
      case 'marketer': return '🚀';
      case 'leader': return '👑';
      default: return '📋';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  if (loading) {
    return (
      <div className="contact-insights-section">
        <h2>Contact Insights</h2>
        <div className="loading-spinner">Loading insights...</div>
      </div>
    );
  }

  if (!insights) {
    return (
      <div className="contact-insights-section">
        <h2>Contact Insights</h2>
        <div className="empty-state">No insights available</div>
      </div>
    );
  }

  return (
    <div className="contact-insights-section">
      <h2>Contact Insights</h2>
      
      {/* Quick Stats Bar */}
      <div className="quick-stats-bar">
        <div className="stat-item">
          <span className="stat-number">{insights.stats.today_completed}</span>
          <span className="stat-label">tasks today</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{insights.stats.week_completed}</span>
          <span className="stat-label">this week</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{insights.stats.month_completed}</span>
          <span className="stat-label">this month</span>
        </div>
      </div>

      {/* Top Active Contacts */}
      <div className="contacts-list">
        <h3>Most Active Contacts</h3>
        {insights.contacts.length > 0 ? (
          <div className="contacts-grid">
            {insights.contacts.map((contact) => (
              <div key={contact.id} className={`contact-card ${contact.is_high_potential ? 'high-potential' : ''}`}>
                <div className="contact-header">
                  <div className="contact-name">{contact.name}</div>
                  {contact.is_high_potential && (
                    <div className="high-potential-badge">⭐ High Potential</div>
                  )}
                </div>
                
                <div className="contact-stage">
                  <span 
                    className="stage-indicator" 
                    style={{ backgroundColor: getStageColor(contact.stage) }}
                  >
                    {getStageIcon(contact.stage)} {contact.stage.charAt(0).toUpperCase() + contact.stage.slice(1)}
                  </span>
                </div>
                
                <div className="contact-progress">
                  <div className="progress-text">
                    {contact.completed_tasks}/{contact.total_tasks} tasks ({contact.completion_percentage}%)
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ 
                        width: `${contact.completion_percentage}%`,
                        backgroundColor: getStageColor(contact.stage)
                      }}
                    ></div>
                  </div>
                </div>
                
                <div className="contact-activity">
                  <div className="activity-item">
                    <span className="activity-label">Last activity:</span>
                    <span className="activity-value">{formatDate(contact.last_activity)}</span>
                  </div>
                  <div className="activity-item">
                    <span className="activity-label">Last interaction:</span>
                    <span className="activity-value">{formatDate(contact.last_interaction)}</span>
                  </div>
                </div>
                
                {contact.is_high_potential && (
                  <div className="potential-indicator">
                    🎯 Likely to advance to next stage
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">No contacts with activity yet</div>
        )}
      </div>
    </div>
  );
}

export default ContactInsights;
